/**
 * Tabs UI component.
 *
 * Implements the WAI-ARIA Authoring Practices tab pattern: roving focus
 * across triggers via Left/Right arrow keys, Home/End jumps to first/last,
 * and Enter/Space activates. Triggers expose `role="tab"`, the list
 * exposes `role="tablist"`, and the panel exposes `role="tabpanel"` with
 * `aria-selected` reflecting the active value.
 */
import * as React from 'react';
import { cn } from '@/lib/utils';

interface TabsContextValue {
  value: string;
  onValueChange: (value: string) => void;
}

const TabsContext = React.createContext<TabsContextValue | undefined>(undefined);

function useTabsContext() {
  const context = React.useContext(TabsContext);
  if (!context) {
    throw new Error('Tabs components must be used within a Tabs provider');
  }
  return context;
}

interface TabsProps {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  children: React.ReactNode;
  className?: string;
}

export function Tabs({
  value: controlledValue,
  defaultValue,
  onValueChange,
  children,
  className,
}: TabsProps) {
  const [uncontrolledValue, setUncontrolledValue] = React.useState(defaultValue ?? '');

  const value = controlledValue ?? uncontrolledValue;
  const handleValueChange = React.useCallback(
    (newValue: string) => {
      if (controlledValue === undefined) {
        setUncontrolledValue(newValue);
      }
      onValueChange?.(newValue);
    },
    [controlledValue, onValueChange]
  );

  return (
    <TabsContext.Provider value={{ value, onValueChange: handleValueChange }}>
      <div className={cn('w-full', className)}>{children}</div>
    </TabsContext.Provider>
  );
}

interface TabsListProps {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

export function TabsList({ children, className, style }: TabsListProps) {
  const listRef = React.useRef<HTMLDivElement>(null);

  // Roving-focus arrow-key navigation per WAI-ARIA tab pattern.
  // Left/Right move focus among tabs (skipping disabled), Home/End jump
  // to the first/last enabled tab. Activation is handled by the
  // browser's native click → onClick on the trigger button.
  const onKeyDown = React.useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    const key = e.key;
    if (
      key !== 'ArrowLeft' &&
      key !== 'ArrowRight' &&
      key !== 'Home' &&
      key !== 'End'
    ) {
      return;
    }
    const list = listRef.current;
    if (!list) return;
    const tabs = Array.from(
      list.querySelectorAll<HTMLButtonElement>('[role="tab"]:not([disabled])'),
    );
    if (tabs.length === 0) return;
    const currentIdx = tabs.indexOf(document.activeElement as HTMLButtonElement);
    let nextIdx = currentIdx;
    if (key === 'ArrowLeft') {
      nextIdx = currentIdx <= 0 ? tabs.length - 1 : currentIdx - 1;
    } else if (key === 'ArrowRight') {
      nextIdx = currentIdx === tabs.length - 1 ? 0 : currentIdx + 1;
    } else if (key === 'Home') {
      nextIdx = 0;
    } else if (key === 'End') {
      nextIdx = tabs.length - 1;
    }
    if (nextIdx !== currentIdx) {
      e.preventDefault();
      tabs[nextIdx].focus();
      // Activate on focus so the panel updates immediately. This matches
      // the "automatic activation" variant of the WAI-ARIA pattern,
      // appropriate for cheap panel switches (no expensive data fetch
      // on selection).
      tabs[nextIdx].click();
    }
  }, []);

  return (
    <div
      ref={listRef}
      className={cn(
        'inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground',
        className
      )}
      style={style}
      role="tablist"
      onKeyDown={onKeyDown}
    >
      {children}
    </div>
  );
}

interface TabsTriggerProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick' | 'value' | 'role' | 'aria-selected' | 'type'> {
  value: string;
  children: React.ReactNode;
}

export function TabsTrigger({
  value,
  children,
  className,
  disabled,
  style,
  ...rest
}: TabsTriggerProps) {
  const { value: selectedValue, onValueChange } = useTabsContext();
  const isSelected = selectedValue === value;

  return (
    <button
      {...rest}
      role="tab"
      type="button"
      aria-selected={isSelected}
      data-state={isSelected ? 'active' : 'inactive'}
      disabled={disabled}
      style={style}
      className={cn(
        'inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
        isSelected
          ? 'bg-background text-foreground shadow-sm'
          : 'hover:bg-background/50 hover:text-foreground',
        className
      )}
      onClick={() => onValueChange(value)}
    >
      {children}
    </button>
  );
}

interface TabsContentProps {
  value: string;
  children: React.ReactNode;
  className?: string;
}

export function TabsContent({ value, children, className }: TabsContentProps) {
  const { value: selectedValue } = useTabsContext();
  const isSelected = selectedValue === value;

  if (!isSelected) return null;

  return (
    <div
      role="tabpanel"
      className={cn(
        'mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        className
      )}
    >
      {children}
    </div>
  );
}
